import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { Landmark, RefreshCw, X, ChevronDown, Upload } from 'lucide-react';
import { api, whyFailed } from '../services/api';
import { useReadOnly } from '../hooks/useReadOnly';
import { invalidateMoney } from '../utils/moneyQueries';

/**
 * Bank purchases → job expenses. Every money-out transaction Plaid pulled shows
 * up here sorted by the matcher: auto-added to a job, waiting for a job, overhead
 * (fuel, insurance…), or skipped (transfers / crew pay). One tap moves a row;
 * "remember" turns the decision into a rule for that merchant.
 */
type Tx = {
  id: number; date: string; name: string; merchant_name: string | null; amount: number; bank_category: string | null; pending: number;
  expense_status: 'new' | 'suggested' | 'assigned' | 'overhead' | 'ignored' | 'personal'; expense_category: string | null; match_reason: string | null; reviewed_at: string | null;
  job_id: number | null; job_customer: string | null; job_date: string | null; job_service: string | null; job_address: string | null;
  suggested_job_id: number | null; suggested_customer: string | null; suggested_date: string | null; suggested_service: string | null; suggested_address: string | null;
  wants_receipt?: number; receipt_count?: number; receipt_id?: number | null;
  /** What kind of cost, in the 2026 categories. */
  category_key?: string | null; category_label?: string | null;
  /** Exactly what it is (the class under the category), when one has been set. */
  expense_class?: string | null; class_key?: string | null; class_label?: string | null;
  /** The lines on the receipt, when one has been read. `key` addresses a line; `cls` is its class. */
  items?: { description: string; total: number; kind?: string; quantity?: number | null; key?: string; cls?: string | null }[];
};
/** A class: exactly what a purchase is, with what choosing it does (the category, the status, whether a job is needed). */
type Cls = { key: string; label: string; group: string; parent: string; hint: string; life_months?: number | null; expense_category: string; expense_status: string; needs_job: boolean; not_a_cost: boolean };
type Candidate = { id: number; date: string; service_type: string | null; title: string | null; status: string; customer_name: string | null; address: string | null };

const QUICK = [
  { key: 'gas', label: 'Gas' },
  { key: 'food', label: 'Food' },
  { key: 'supplies', label: 'Shop' },
  { key: 'equipment', label: 'Equipment' },
  { key: 'admin', label: 'Admin' },
  { key: 'marketing', label: 'Marketing' },
];
const money = (n: number | null | undefined) => `$${Number(n || 0).toFixed(2)}`;
const day = (iso?: string | null) => iso ? new Date(String(iso).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
/**
 * The KAM 2026 categories, straight off Michael's own expense sheets. The list
 * lives on the server next to the costing engine, so what you pick here and
 * what the engine does with it can never drift apart.
 */
type Cat = { key: string; label: string; hint: string; on_job: boolean; bucket: string; capital?: boolean };

function useCategories() {
  const { data } = useQuery('expense-categories', () =>
    api.get('/expenses/categories').then(r => r.data as { categories: Cat[]; grouped: { group: string; items: Cat[] }[]; classes?: Cls[]; class_groups?: { group: string; items: Cls[] }[] }),
    { staleTime: 60 * 60 * 1000 });
  const all = data?.categories || [];
  const classes = data?.classes || [];
  return {
    all,
    grouped: data?.grouped || [],
    onJob: all.filter(c => c.on_job),
    label: (k?: string | null) => all.find(c => c.key === k)?.label || k || '',
    needsJob: (k?: string | null) => !!all.find(c => c.key === k)?.on_job,
    classes,
    classGroups: data?.class_groups || [],
    classOf: (k?: string | null) => classes.find(c => c.key === k) || null,
    classLabel: (k?: string | null) => classes.find(c => c.key === k)?.label || '',
  };
}

export default function BankExpensesPanel({ range }: { range: { start: string; end: string } }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const [picking, setPicking] = useState<Tx | null>(null);
  // The class a job is being picked for ("rental for a job" needs a job before it can be filed).
  const [pickingClass, setPickingClass] = useState<string | null>(null);
  const [receiptFor, setReceiptFor] = useState<Tx | null>(null);
  const [splitting, setSplitting] = useState<Tx | null>(null);
  const [showFiled, setShowFiled] = useState(false);
  const [moreId, setMoreId] = useState<number | null>(null);
  const [gone, setGone] = useState<number[]>([]);
  const remember = true;
  const cats = useCategories();

  const params = { ...(range.start ? { start: range.start } : {}), ...(range.end ? { end: range.end } : {}) };
  const { data: summary } = useQuery(['expenses-summary', params], () => api.get('/expenses/summary', { params }).then(r => r.data));
  // One list, no tabs. The sorter files every charge into a bucket already, so
  // this is a list to skim and correct — not a queue to clear.
  const { data, isLoading, isError, refetch } = useQuery(['expenses-inbox', params], () =>
    api.get('/expenses/inbox', { params }).then(r => r.data.transactions as Tx[]));
  // Not-yet-confirmed (auto) on top, biggest first — a $955 charge in the wrong
  // bucket distorts the books; a $3.48 Gatorade does not.
  const rows = React.useMemo(() => [...(data || [])].sort((a, b) => Number(b.amount) - Number(a.amount)), [data]);
  const open = rows.filter(r => !r.reviewed_at && !gone.includes(r.id));
  const filed = rows.filter(r => r.reviewed_at);
  const shown = showFiled ? filed : open;

  // A sort moves job costs, overhead, the month and the unsorted count — refresh every money figure.
  const refresh = () => invalidateMoney(qc);
  const act = useMutation(
    (v: { id: number; action: 'assign' | 'overhead' | 'ignore' | 'personal' | 'undo' | 'class'; body?: any }) => api.post(`/expenses/${v.id}/${v.action}`, { ...(v.body || {}), remember }),
    { onSuccess: (r: any) => { toast.success(r.data?.message || 'Done'); setPicking(null); setPickingClass(null); refresh(); },
      onError: (e: any, v) => { setGone(g => g.filter(id => id !== v.id)); toast.error(whyFailed(e, 'Could not file that charge')); } });
  // One charge over several jobs — a rental used on three jobs (owner, Oct 22 2026). One job cost per job, the charge on each.
  const shareMany = useMutation(
    (v: { id: number; job_ids: number[]; category: string; mode: 'equal' | 'amounts'; amounts: Record<string, string> }) =>
      api.post(`/expenses/${v.id}/share`, { job_ids: v.job_ids, category: v.category, mode: v.mode,
        amounts: Object.fromEntries(Object.entries(v.amounts).map(([k, a]) => [k, a === '' || a == null ? null : Number(a)])) }),
    { onSuccess: (r: any) => { toast.success(r.data?.message || 'Split'); setPicking(null); setPickingClass(null); refresh(); },
      onError: (e: any, v) => { setGone(g => g.filter(id => id !== v.id)); toast.error(whyFailed(e, 'Could not split that')); } });
  const sync = useMutation(
    async () => { const s = await api.post('/banking/sync').catch(() => null); const m = await api.post('/expenses/match'); return { s, m }; },
    { onSuccess: (r: any) => { toast.success(r.s?.data?.message ? `${r.s.data.message}` : r.m.data?.message); refresh(); },
      onError: (e: any) => { toast.error(whyFailed(e, 'Sync failed')); } });

  // The bucket a row sits in now, as a category key for the dropdown.
  const currentCat = (t: Tx): string =>
    t.expense_status === 'ignored' ? (t.expense_category === 'crew_pay' ? '__crew' : t.expense_category === 'transfer' ? '__internal' : '__skip')
    : t.expense_status === 'personal' ? 'personal'
    // No category yet means no guess — not a silent "supplies" that shows as
    // chosen and can't then be picked (owner, Sep 29 2026: "I can't select
    // supplies on More").
    : (t.category_key || (t.expense_status === 'assigned' ? 'material' : ''));

  // Exactly what it is. A job class needs a job; if there isn't one yet, open the picker.
  function chooseClass(t: Tx, cls: Cls) {
    const file = () => setGone(g => g.includes(t.id) ? g : [...g, t.id]);
    if (cls.needs_job) {
      const jobId = t.job_id || t.suggested_job_id;
      if (jobId) { file(); act.mutate({ id: t.id, action: 'class', body: { expense_class: cls.key, job_id: jobId } }); }
      else { setPickingClass(cls.key); setPicking(t); }
      return;
    }
    file();
    act.mutate({ id: t.id, action: 'class', body: { expense_class: cls.key } });
  }
  const itemClass = useMutation(
    (v: { receiptId: number; key: string; cls: string | null }) => api.post(`/expenses/receipt/${v.receiptId}/item-class`, { key: v.key, expense_class: v.cls }),
    { onSuccess: (r: any) => { toast.success(r.data?.message || 'Done'); refresh(); },
      onError: (e: any) => { toast.error(whyFailed(e, 'Could not set what that line was')); } });

  // One dropdown → the right action. An on-job bucket needs a job; if there
  // isn't one yet, open the picker rather than guess a wrong one.
  function chooseBucket(t: Tx, cat: string) {
    const file = () => setGone(g => g.includes(t.id) ? g : [...g, t.id]);
    if (cat === '__skip') { file(); return act.mutate({ id: t.id, action: 'ignore' }); }
    const cls = cats.classOf(cat);
    if (cls) return chooseClass(t, cls);
    if (cat === '__crew') { file(); return act.mutate({ id: t.id, action: 'ignore', body: { category: 'crew_pay' } }); }
    if (cat === '__internal') { file(); return act.mutate({ id: t.id, action: 'ignore', body: { category: 'transfer' } }); }
    if (cat === 'personal') { file(); return act.mutate({ id: t.id, action: 'personal' }); }
    if (cats.needsJob(cat)) {
      const jobId = t.job_id || t.suggested_job_id;
      if (jobId) { file(); act.mutate({ id: t.id, action: 'assign', body: { job_id: jobId, category: cat } }); }
      else setPicking(t);
      return;
    }
    file();
    act.mutate({ id: t.id, action: 'overhead', body: { category: cat } });
  }

  return (
    <div id="bank-expenses" className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Landmark className="h-5 w-5 text-brand-600" />
          <div>
            <p className="font-semibold text-gray-900">Bank expenses</p>
            <p className="text-xs text-gray-500">{open.length ? `${open.length} still need a look. Tap where it went.` : 'Nothing left to sort.'}</p>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {summary?.overhead_total > 0 && <span className="text-xs text-gray-500">Overhead <b className="text-gray-800">{money(summary.overhead_total)}</b></span>}
          {summary?.crew_pay_total > 0 && <span className="text-xs text-gray-500">Crew pay <b className="text-gray-800">{money(summary.crew_pay_total)}</b></span>}
          {summary?.personal_total > 0 && <span className="text-xs text-gray-500">Personal <b className="text-gray-800">{money(summary.personal_total)}</b></span>}
          <button onClick={() => sync.mutate()} disabled={readOnly || sync.isLoading} title={readOnly ? 'View-only account' : undefined} className="text-xs font-medium border border-gray-300 rounded-lg px-3 py-1.5 bg-white hover:bg-gray-50 flex items-center gap-1.5 disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${sync.isLoading ? 'animate-spin' : ''}`} /> Sync &amp; sort
          </button>
        </div>
      </div>

      {!readOnly && !showFiled && open.length > 1 && <ClearInOneGo rows={open} onDone={refresh} />}

      {isLoading ? <div className="p-6 text-center text-gray-500 text-sm">Loading…</div>
        : isError ? (
          <div className="p-6 text-center text-sm text-red-600">
            Could not load the bank purchases. <button type="button" onClick={() => refetch()} className="underline font-semibold">Try again</button>
          </div>
        )
        : rows.length === 0 ? <div className="p-6 text-center text-gray-500 text-sm">No purchases in this range — hit “Sync &amp; sort”.</div>
        : open.length === 0 && !showFiled ? <div className="p-6 text-center text-gray-500 text-sm">All sorted.</div>
        : (
          <ul className="divide-y divide-gray-100">
            {shown.map(t => {
              const jobId = t.job_id || t.suggested_job_id;
              const who = t.job_customer || t.suggested_customer;
              const chip = 'press text-sm px-3 py-2 rounded-xl border border-gray-300 text-gray-800 font-semibold';
              return (
                <li key={t.id} className="px-4 py-3 space-y-2">
                  <div className="flex items-baseline gap-3">
                    <p className="flex-1 min-w-0 text-sm font-medium text-gray-900 break-words">
                      {t.merchant_name || t.name}
                      {isNameless(t) && t.name && t.name !== t.merchant_name && <span className="block text-[11px] font-normal text-gray-500">{t.name} · the bank left off the store — tap Split to find its receipt</span>}
                    </p>
                    <p className="text-base font-bold text-gray-900">{money(t.amount)}</p>
                  </div>
                  <p className="text-xs text-gray-500">{day(t.date)}{!showFiled && cats.label(currentCat(t)) ? ` · guessed ${t.class_label || cats.label(currentCat(t))}` : ''}{showFiled && (t.class_label || t.category_key) ? ` · ${t.class_label || cats.label(t.category_key)}` : ''}</p>
                  {showFiled && t.job_customer && (
                    <p className="text-xs text-gray-700">On <b>{t.job_customer}</b>{t.job_date ? ` · ${day(t.job_date)}` : ''}{t.job_address ? ` · ${t.job_address}` : ''}</p>
                  )}
                  {!!t.items?.length && <ReceiptLines items={t.items} classes={cats.classGroups} onClass={!readOnly && t.receipt_id ? (key, cls) => itemClass.mutate({ receiptId: t.receipt_id as number, key, cls }) : undefined} />}
                  {!showFiled && !readOnly && (
                    <div className="flex flex-wrap gap-1.5">
                      <button onClick={() => {
                        if (!jobId) { setPicking(t); return; }
                        setGone(g => g.includes(t.id) ? g : [...g, t.id]);
                        act.mutate({ id: t.id, action: 'assign', body: { job_id: jobId, category: cats.needsJob(t.category_key) ? t.category_key : 'material' } });
                      }}
                        className="press text-sm px-3 py-2 rounded-xl bg-brand-600 text-black font-semibold">
                        {who ? `On ${who.split(' ')[0]}` : 'On a job'}
                      </button>
                      {who && <button onClick={() => setPicking(t)} className={chip}>Other job</button>}
                      {QUICK.map(q => (
                        <button key={q.key} onClick={() => { setGone(g => [...g, t.id]); act.mutate({ id: t.id, action: 'overhead', body: { category: q.key } }); }}
                          className={currentCat(t) === q.key ? 'press text-sm px-3 py-2 rounded-xl border-2 border-brand-600 text-gray-900 font-semibold' : chip}>
                          {q.label}
                        </button>
                      ))}
                      <button onClick={() => { setGone(g => [...g, t.id]); act.mutate({ id: t.id, action: 'personal' }); }} className={currentCat(t) === 'personal' ? 'press text-sm px-3 py-2 rounded-xl border-2 border-brand-600 text-gray-900 font-semibold' : chip}>Personal</button>
                      <button onClick={() => { setGone(g => [...g, t.id]); act.mutate({ id: t.id, action: 'ignore', body: { category: 'crew_pay' } }); }} className={currentCat(t) === '__crew' ? 'press text-sm px-3 py-2 rounded-xl border-2 border-brand-600 text-gray-900 font-semibold' : chip}>Crew pay</button>
                      <button onClick={() => { setGone(g => [...g, t.id]); act.mutate({ id: t.id, action: 'ignore', body: { category: 'transfer' } }); }} className={currentCat(t) === '__internal' ? 'press text-sm px-3 py-2 rounded-xl border-2 border-brand-600 text-gray-900 font-semibold' : chip}>Internal</button>
                      <button onClick={() => { setGone(g => [...g, t.id]); act.mutate({ id: t.id, action: 'ignore' }); }} className="press text-sm px-3 py-2 rounded-xl text-gray-500 font-semibold">Skip</button>
                      <button onClick={() => setMoreId(moreId === t.id ? null : t.id)} className="press text-sm px-2 py-2 text-gray-500">More</button>
                      <button onClick={() => (!t.items?.length && (t.wants_receipt || readsEmail(t))) ? setReceiptFor(t) : setSplitting(t)} className="press text-sm px-2 py-2 text-gray-500">Split</button>
                    </div>
                  )}
                  {!showFiled && !readOnly && moreId === t.id && (
                    // Starts blank so every choice is a change — including the guess itself.
                    <select value="" onChange={e => { if (e.target.value) chooseBucket(t, e.target.value); }} disabled={act.isLoading}
                      className="border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white w-full">
                      <option value="">{currentCat(t) && cats.label(currentCat(t)) ? `What exactly was it? (guess: ${t.class_label || cats.label(currentCat(t))})` : 'What exactly was it?'}</option>
                      <ClassOptions groups={cats.classGroups} />
                      <optgroup label="Or just the pile">
                        {cats.grouped.flatMap(g => g.items).map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                        <option value="__skip">Skip</option>
                      </optgroup>
                    </select>
                  )}
                  {showFiled && !readOnly && (
                    <div className="flex flex-wrap items-center gap-3">
                      {/* Wrong yard? Move it. A receipt goes line by line; a plain charge to one job. */}
                      <button onClick={() => t.items?.length ? setSplitting(t) : setPicking(t)} disabled={act.isLoading}
                        className="text-xs font-semibold text-brand-700 underline">
                        {t.job_id ? 'Change job' : 'Put on a job'}
                      </button>
                      {!t.items?.length && readsEmail(t) && (
                        <button onClick={() => setReceiptFor(t)} className="text-xs text-gray-600 underline">Add receipt</button>
                      )}
                      <button onClick={() => act.mutate({ id: t.id, action: 'undo' })} disabled={act.isLoading} className="text-xs text-gray-500 underline">Undo</button>
                      {/* Exactly what it was — the class under the pile it is in. */}
                      <select value={t.class_key || ''} onChange={e => { const c = cats.classOf(e.target.value); if (c) chooseClass(t, c); }} disabled={act.isLoading}
                        className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white max-w-[14rem]" aria-label="Exactly what it was">
                        <option value="">{t.class_key ? '' : 'What exactly was it?'}</option>
                        <ClassOptions groups={cats.classGroups} />
                      </select>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

      {filed.length > 0 && (
        <button type="button" onClick={() => setShowFiled(v => !v)}
          className="w-full text-left px-4 py-3 text-sm text-gray-600 border-t border-gray-100">
          {showFiled ? 'Back to the ones that need a look' : `${filed.length} already filed`}
        </button>
      )}

      <WaitingReceipts />

      <details className="px-4 py-3 border-t border-gray-100">
        <summary className="text-xs text-gray-500 cursor-pointer">By class — exactly what every dollar was</summary>
        <ByClass range={range} onChanged={refresh} />
      </details>

      {!readOnly && (
        <details className="px-4 py-3 border-t border-gray-100">
          <summary className="text-xs text-gray-500 cursor-pointer">Import receipts — Home Depot and Amazon exports</summary>
          <div className="mt-3 space-y-3">
            <HomeDepotBackfill onFiled={refresh} />
            <AmazonImport onFiled={refresh} />
          </div>
        </details>
      )}

      {splitting && <SplitAcrossJobs tx={splitting} onClose={() => setSplitting(null)} onDone={() => { setSplitting(null); refresh(); }} />}
      {receiptFor && (readsEmail(receiptFor)
        ? <HomeDepotReceipt tx={receiptFor} onClose={() => setReceiptFor(null)} onDone={() => { setReceiptFor(null); refresh(); }} />
        : <ReceiptSplitter tx={receiptFor} onClose={() => setReceiptFor(null)} onDone={() => { setReceiptFor(null); refresh(); }} />)}
      {picking && <JobPicker tx={picking} onClose={() => { setPicking(null); setPickingClass(null); }} onPick={(jobId, cat) => {
        setGone(g => g.includes(picking.id) ? g : [...g, picking.id]);
        if (pickingClass) act.mutate({ id: picking.id, action: 'class', body: { expense_class: pickingClass, job_id: jobId } });
        else act.mutate({ id: picking.id, action: 'assign', body: { job_id: jobId, category: cat } });
      }} onPickMany={pickingClass ? undefined : (jobIds, cat, mode, amounts) => {
        setGone(g => g.includes(picking.id) ? g : [...g, picking.id]);
        shareMany.mutate({ id: picking.id, job_ids: jobIds, category: cat, mode, amounts });
      }} busy={act.isLoading || shareMany.isLoading} />}
    </div>
  );
}

/** Jobs matching a customer name or address, around the charge's date. */
function useJobSearch(txId: number, q: string): Candidate[] {
  const term = q.trim();
  const { data } = useQuery(['expense-job-search', txId, term],
    () => api.get(`/expenses/${txId}/candidates`, { params: { q: term } }).then(r => (r.data.found || []) as Candidate[]),
    { enabled: term.length >= 2, keepPreviousData: true });
  return term.length >= 2 ? (data || []) : [];
}

/**
 * Which job — or, with onPickMany, which jobs: "Several jobs" turns every row into a tick, and the charge is spread
 * over the picked ones in equal shares or the amounts he types (owner, Oct 22 2026: "rental for multiple jobs").
 */
function JobPicker({ tx, onClose, onPick, onPickMany, busy }: { tx: Tx; onClose: () => void; onPick: (jobId: number, category: string) => void;
  onPickMany?: (jobIds: number[], category: string, mode: 'equal' | 'amounts', amounts: Record<string, string>) => void; busy: boolean }) {
  const cats = useCategories();
  const [cat, setCat] = useState(cats.needsJob(tx.category_key) ? (tx.category_key as string) : 'material');
  const [showMowing, setShowMowing] = useState(false);
  const [q, setQ] = useState('');
  const [many, setMany] = useState(false);
  const [picked, setPicked] = useState<Candidate[]>([]);
  const [mode, setMode] = useState<'equal' | 'amounts'>('equal');
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const has = (id: number) => picked.some(p => p.id === id);
  const charge = Number(tx.amount) || 0;
  const each = picked.length ? Math.round((charge / picked.length) * 100) / 100 : 0;
  const typed = Math.round(picked.reduce((t, p) => t + (Number(amounts[String(p.id)]) || 0), 0) * 100) / 100;
  const { data, isLoading } = useQuery(['expense-candidates', tx.id], () => api.get(`/expenses/${tx.id}/candidates`).then(r => r.data as { non_mowing: Candidate[]; mowing_that_day: Candidate[] }));
  const found = useJobSearch(tx.id, q);
  const list = (l: Candidate[]) => l.map(j => (
    <button key={j.id} onClick={() => (many ? setPicked(p => has(j.id) ? p.filter(x => x.id !== j.id) : [...p, j]) : onPick(j.id, cat))} disabled={busy}
      className={`w-full text-left px-3 py-2 rounded-lg border ${many && has(j.id) ? 'border-brand-500 bg-brand-50' : 'border-gray-200 hover:border-brand-400 hover:bg-brand-50'} disabled:opacity-50`}>
      <p className="text-sm font-medium text-gray-900">{many && has(j.id) ? '✓ ' : ''}{j.customer_name || `Job #${j.id}`} <span className="text-gray-500 font-normal">· {day(j.date)}{j.service_type ? ` · ${j.service_type}` : ''}{j.title ? ` · ${j.title}` : ''}</span></p>
      {j.address && <p className="text-xs text-gray-500">{j.address.split(',')[0]}</p>}
    </button>
  ));
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-5 space-y-3 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">{many ? 'Which jobs share this?' : 'Which job was this for?'}</h3>
            <p className="text-xs text-gray-500">{tx.merchant_name || tx.name} · {day(tx.date)} · {money(tx.amount)}</p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>
        {onPickMany && (
          <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs font-medium">
            <button type="button" onClick={() => setMany(false)} className={`press flex-1 py-2 ${!many ? 'bg-brand-600 text-black' : 'bg-white text-gray-700'}`}>One job</button>
            <button type="button" onClick={() => setMany(true)} className={`press flex-1 py-2 border-l border-gray-300 ${many ? 'bg-brand-600 text-black' : 'bg-white text-gray-700'}`}>Several jobs — split the cost</button>
          </div>
        )}
        {many && (
          <div className="rounded-xl border border-brand-200 bg-brand-50/60 px-3 py-2.5 space-y-2">
            <p className="text-xs font-semibold text-gray-700">{picked.length ? `${picked.length} picked — tap a job below to add or drop it` : 'Tap the jobs below that share this cost'}</p>
            {picked.length > 0 && (
              <>
                <div className="flex gap-3 text-xs text-gray-700">
                  <label className="flex items-center gap-1"><input type="radio" checked={mode === 'equal'} onChange={() => setMode('equal')} className="text-brand-600" /> Equal — {money(each)} each</label>
                  <label className="flex items-center gap-1"><input type="radio" checked={mode === 'amounts'} onChange={() => setMode('amounts')} className="text-brand-600" /> Amounts I type</label>
                </div>
                <ul className="space-y-1">
                  {picked.map((p, i) => (
                    <li key={p.id} className="flex items-center gap-2 text-xs text-gray-800">
                      <span className="flex-1 truncate">{p.customer_name || `Job #${p.id}`} · {day(p.date)}{p.title ? ` · ${p.title}` : ''}</span>
                      {mode === 'amounts'
                        ? <span className="inline-flex items-center gap-1">$<input type="number" min={0} step="0.01" value={amounts[String(p.id)] ?? ''} placeholder={i === picked.length - 1 ? 'the rest' : ''}
                            onChange={e => setAmounts(a => ({ ...a, [String(p.id)]: e.target.value }))} className="w-24 border border-gray-300 rounded-lg px-2 py-1 text-xs" /></span>
                        : <span className="font-semibold">{money(i === picked.length - 1 ? Math.round((charge - each * (picked.length - 1)) * 100) / 100 : each)}</span>}
                    </li>
                  ))}
                </ul>
                {mode === 'amounts' && <p className="text-[11px] text-gray-500">Typed so far {money(typed)} of {money(charge)} — leave the last one blank and it takes the rest.</p>}
                <button type="button" disabled={busy || picked.length < 2} onClick={() => onPickMany?.(picked.map(p => p.id), cat, mode, amounts)}
                  className="press w-full text-sm px-3 py-2 rounded-xl bg-brand-600 text-black font-semibold disabled:opacity-50">
                  {picked.length < 2 ? 'Pick at least two jobs' : `Split ${money(charge)} across ${picked.length} jobs`}
                </button>
              </>
            )}
          </div>
        )}
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">What kind of cost</p>
        <div className="flex gap-1.5">
          {cats.onJob.map(c => (
            <button key={c.key} onClick={() => setCat(c.key)} title={c.hint}
              className={`press text-xs px-2.5 py-1.5 rounded-full border ${cat === c.key ? 'bg-brand-600 text-black border-brand-600' : 'border-gray-200 text-gray-600'}`}>
              {c.label}
            </button>
          ))}
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Find a job — customer or address"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        {q.trim().length >= 2 && (
          found.length ? <div className="space-y-1.5">{list(found)}</div> : <p className="text-sm text-gray-500">No job matches “{q}” within four months.</p>
        )}
        {isLoading ? <p className="text-sm text-gray-500">Loading jobs…</p> : (
          <>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Projects & one-offs within 2 weeks</p>
            {data?.non_mowing?.length ? <div className="space-y-1.5">{list(data.non_mowing)}</div> : <p className="text-sm text-gray-500">None — a purchase like this usually belongs to a project.</p>}
            <button onClick={() => setShowMowing(v => !v)} className="text-xs text-gray-500 flex items-center gap-1"><ChevronDown className={`h-3.5 w-3.5 transition-transform ${showMowing ? '' : '-rotate-90'}`} /> Mowing stops that day ({data?.mowing_that_day?.length || 0})</button>
            {showMowing && <div className="space-y-1.5">{list(data?.mowing_that_day || [])}</div>}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * One Home Depot trip, several jobs.
 *
 * The bank only knows the total, so the till receipt is photographed, read, and
 * each line sent to the job it was really for. Lines may add up to less than
 * the charge — tax, or a tool that is overhead — but never more.
 */

/**
 * Most of a review queue is not a queue of decisions.
 *
 * Twelve purchases from the mulch yard are one decision made twelve times. Nine
 * from Amazon are usually one decision too. Presenting them as 102 separate
 * rows, each with five buttons, turns half an hour of judgement into an evening
 * of clicking — and the clicking is where mistakes come from, because by row
 * sixty nobody is reading any more.
 *
 * So merchants seen more than once are offered as a group: accept every
 * suggestion, or send the lot to overhead. Whatever is chosen is remembered, so
 * next month's purchases from that merchant sort themselves and the queue never
 * grows back.
 *
 * Merchants that genuinely vary — Home Depot, where one trip is mulch for Gwen
 * and a valve for Megan — are deliberately left out of this. They need the
 * line-by-line split, and a bulk button would only make it easy to get them
 * wrong quickly.
 */
const VARIES = /home\s*depot|lowe|mccoy/i;

function ClearInOneGo({ rows, onDone }: { rows: Tx[]; onDone: () => void }) {
  const cats = useCategories();
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState<Set<string>>(new Set());

  const groups = React.useMemo(() => {
    const by = new Map<string, Tx[]>();
    for (const t of rows) {
      const name = (t.merchant_name || t.name || '').trim();
      if (!name || VARIES.test(name)) continue;
      const key = name.toLowerCase();
      by.set(key, [...(by.get(key) || []), t]);
    }
    return Array.from(by.entries())
      .filter(([k, v]) => v.length > 1 && !done.has(k))
      .map(([key, txs]) => ({
        key, name: txs[0].merchant_name || txs[0].name,
        txs, total: txs.reduce((s, t) => s + Number(t.amount), 0),
        withJob: txs.filter(t => t.suggested_job_id).length,
      }))
      .sort((a, b) => b.total - a.total);
  }, [rows, done]);

  if (!groups.length) return null;

  const covered = groups.reduce((s, g) => s + g.txs.length, 0);

  async function apply(g: any, action: 'assign' | 'overhead' | 'personal' | 'ignore', category?: string) {
    setBusy(g.key);
    let ok = 0, skipped = 0;
    for (const t of g.txs) {
      try {
        if (action === 'assign') {
          if (!t.suggested_job_id) { skipped++; continue; }
          await api.post(`/expenses/${t.id}/assign`, {
            job_id: t.suggested_job_id,
            category: t.category_key && cats.needsJob(t.category_key) ? t.category_key : 'material',
            remember: ok === 0,
          });
        } else {
          await api.post(`/expenses/${t.id}/${action}`, {
            ...(action === 'overhead' ? { category: category || t.expense_category || 'supplies' } : action === 'ignore' && category ? { category } : {}),
            remember: ok === 0,
          });
        }
        ok++;
      } catch { skipped++; }
    }
    setBusy(null);
    // Nothing went through (a refusal, a dropped connection): say so and keep the group on screen.
    if (ok === 0) { toast.error(`${g.name}: nothing sorted${skipped ? ` — ${skipped} left for you` : ''}`); return; }
    setDone(p => new Set(p).add(g.key));
    toast.success(`${g.name}: ${ok} sorted${skipped ? `, ${skipped} left for you` : ''} · remembered for next time`);
    onDone();
  }

  return (
    <div className="mx-4 mt-3 rounded-xl border border-brand-200 bg-brand-50 p-3 space-y-2">
      <p className="text-sm font-semibold text-brand-900">
        Clear {covered} of these in {groups.length} decision{groups.length === 1 ? '' : 's'}
      </p>
      <p className="text-xs text-brand-800">
        Same merchant, same answer every time. Whatever you pick is remembered, so next month sorts itself.
      </p>
      <ul className="space-y-1.5">
        {groups.map(g => (
          <li key={g.key} className="bg-white rounded-lg border border-brand-100 px-3 py-2 flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[11rem]">
              <p className="text-sm font-medium text-gray-900">{g.name}</p>
              <p className="text-[11px] text-gray-500">
                {g.txs.length} purchases · {money(g.total)}
                {g.withJob < g.txs.length && ` · ${g.txs.length - g.withJob} with no job nearby`}
              </p>
            </div>
            {busy === g.key ? <span className="text-xs text-gray-500">Sorting…</span> : (
              <div className="flex flex-wrap gap-1.5">
                {g.withJob > 0 && (
                  <button onClick={() => apply(g, 'assign')}
                    className="press text-xs px-2.5 py-1.5 rounded-xl bg-brand-600 text-black font-semibold"
                    title="Put each one on the job the app suggested for it">
                    On a job ({g.withJob})
                  </button>
                )}
                {QUICK.map(q => (
                  <button key={q.key} onClick={() => apply(g, 'overhead', q.key)} className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-700 font-medium">{q.label}</button>
                ))}
                <button onClick={() => apply(g, 'personal')} className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-700 font-medium">Personal</button>
                <button onClick={() => apply(g, 'ignore', 'crew_pay')} className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-700 font-medium">Crew pay</button>
                <button onClick={() => apply(g, 'ignore', 'transfer')} className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-700 font-medium">Internal</button>
                <button onClick={() => setDone(p => new Set(p).add(g.key))} className="text-xs px-2 py-1.5 text-gray-500">one by one</button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * One charge, several jobs.
 *
 * The trip is nearly always mostly one job plus a few things that were never
 * that job's cost — a knife for the truck, a bucket, drinks from the cooler by
 * the till. So it opens with a guess: every line that looks like yard material
 * goes to the nearest job, the tools and the drinks go nowhere, and the whole
 * thing is one button if the guess is right.
 *
 * A line can be charged for less than it cost. Half a jug of weedkiller on one
 * lawn is half a jug still on the shelf, and charging the whole thing there
 * makes that job look unprofitable while every other lawn sprayed from the
 * same jug looks free.
 */

/** What the receipt reader called it → what it is in the 2026 categories. */
const KIND_TO_CATEGORY: Record<string, string> = {
  material: 'material',
  supplies: 'supplies_used',
  tool: 'equipment',
  rental: 'equipment',
  fuel: 'gas',
  other: 'material',
};

/** Things off the shelf by the till that were never any yard's cost. */
const SNACK = /gatorade|powerade|monster|coke|pepsi|water|energy|soda|snack|candy|nuts|peanut|jerky|drink|beverage|coffee/i;
const TRUCK = /knife|pick set|tape measure|glove|marker|sharpie|bucket|hook|clamp|blade|bit set|sandpaper|zip tie/i;

function guessCategory(item: any): string {
  const d = String(item?.description || '');
  if (SNACK.test(d)) return 'food';
  if (TRUCK.test(d)) return 'supplies';
  return KIND_TO_CATEGORY[item?.kind] || 'material';
}

function SplitAcrossJobs({ tx, onClose, onDone }: { tx: Tx; onClose: () => void; onDone: () => void }) {
  const cats = useCategories();
  const charge = Number(tx.amount) || 0;
  const items = React.useMemo(() => tx.items || [], [tx.items]);
  const byLine = items.length > 0;

  const { data: cand } = useQuery(['expense-candidates', tx.id], () =>
    api.get(`/expenses/${tx.id}/candidates`).then(r => r.data as { non_mowing: Candidate[]; mowing_that_day: Candidate[] }));
  const [q, setQ] = useState('');
  const found = useJobSearch(tx.id, q);
  // Keep any job picked from a search in the list after the search changes.
  const [extra, setExtra] = useState<Candidate[]>([]);
  React.useEffect(() => { if (found.length) setExtra(p => [...p, ...found.filter(f => !p.some(x => x.id === f.id))]); }, [found]);
  const base = [...(cand?.non_mowing || []), ...(cand?.mowing_that_day || [])];
  const jobs = [...base, ...extra.filter(e => !base.some(b => b.id === e.id))];
  const likely = tx.suggested_job_id || tx.job_id || base[0]?.id || null;

  const [rows, setRows] = useState<any[]>([]);
  const [seeded, setSeeded] = useState(false);

  // Seed once the candidate jobs are known, so the guess can actually point
  // somewhere. Biggest line first: that is the one worth getting right.
  React.useEffect(() => {
    if (seeded) return;
    if (byLine) {
      const sorted = [...items].sort((a, b) => Math.abs(Number(b.total) || 0) - Math.abs(Number(a.total) || 0));
      setRows(sorted.map(i => {
        const cat = guessCategory(i);
        const onJob = cat === 'material' || cat === 'supplies_used';
        return {
          description: i.description,
          full: Math.abs(Number(i.total) || 0),
          amount: Math.abs(Number(i.total) || 0).toFixed(2),
          category: cat,
          job_id: onJob && likely ? String(likely) : '',
        };
      }));
    } else {
      setRows([
        { description: '', full: 0, amount: '', category: 'material', job_id: likely ? String(likely) : '' },
        { description: '', full: 0, amount: '', category: 'material', job_id: '' },
      ]);
    }
    if (byLine || cand) setSeeded(true);
  }, [items, byLine, likely, cand, seeded]);

  const set = (i: number, k: string, v: string) => setRows(p => p.map((x, n) => {
    if (n !== i) return x;
    const next = { ...x, [k]: v };
    // Choosing a running cost means it is nobody's job cost.
    if (k === 'category' && !cats.needsJob(v)) next.job_id = '';
    return next;
  }));

  const filled = rows.filter(r => Number(r.amount) > 0 && r.job_id);
  const used = Math.round(filled.reduce((s, r) => s + Number(r.amount), 0) * 100) / 100;
  const left = Math.round((charge - used) * 100) / 100;
  const jobCount = new Set(filled.map(r => r.job_id)).size;

  /**
   * Everything that was bought but is not this job's cost: whole lines marked
   * as a running cost, and the part of a job line charged for less than it
   * cost — the half jug still on the shelf.
   */
  const runningLines = React.useMemo(() => {
    const out: { amount: number; category: string }[] = [];
    for (const r of rows) {
      const onJob = cats.needsJob(r.category);
      if (!onJob) {
        const amt = Number(r.amount) || r.full || 0;
        if (amt > 0) out.push({ amount: Math.round(amt * 100) / 100, category: r.category });
      } else if (r.job_id && r.full > 0) {
        const unused = Math.round((r.full - (Number(r.amount) || 0)) * 100) / 100;
        // Left on the shelf, so it is stock — Supplies — not this yard's cost.
        if (unused > 0.005) out.push({ amount: unused, category: 'supplies' });
      }
    }
    return out;
  }, [rows, cats]);
  const shelved = Math.round(runningLines.reduce((s, r) => s + r.amount, 0) * 100) / 100;

  /** Everything that could belong to a yard goes to this one. */
  const allTo = (jobId: string) => setRows(p => p.map(r => cats.needsJob(r.category) ? { ...r, job_id: jobId } : r));

  const save = useMutation(
    () => api.post(`/expenses/${tx.id}/split`, {
      lines: filled.map(r => ({
        description: (r.description || `${tx.merchant_name || tx.name} — part`).slice(0, 200),
        amount: Number(r.amount), category: r.category, job_id: Number(r.job_id),
      })),
      // What was bought but not used up here. It is still money spent, so it is
      // kept as a running cost rather than falling out of the books.
      running: runningLines,
    }),
    { onSuccess: (r: any) => { toast.success(r.data.message); onDone(); },
      onError: (e: any) => { toast.error(whyFailed(e, 'Could not split that')); } });

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="p-5 pb-3 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-bold text-gray-900">{byLine ? 'Where did this trip go?' : 'Split across jobs'}</h3>
              <p className="text-xs text-gray-500">{tx.merchant_name || tx.name} · {day(tx.date)} · {money(charge)}</p>
            </div>
            <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
          </div>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Job not listed? Find it — customer or address"
            className="mt-3 w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
          {q.trim().length >= 2 && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs mt-2">
              {found.length ? found.slice(0, 6).map(j => (
                <button key={j.id} type="button" onClick={() => { allTo(String(j.id)); setQ(''); }}
                  className="px-2 py-1 rounded-lg border border-brand-300 bg-brand-50 text-gray-800 font-medium">
                  {j.customer_name || `#${j.id}`} · {day(j.date)}
                </button>
              )) : <span className="text-gray-500">No job matches “{q}”.</span>}
            </div>
          )}
          {byLine && jobs.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs mt-3">
              <span className="text-gray-500">All the yard stuff to:</span>
              {jobs.slice(0, 3).map(j => (
                <button key={j.id} type="button" onClick={() => allTo(String(j.id))}
                  className="px-2 py-1 rounded-lg border border-gray-300 text-gray-700 font-medium hover:border-brand-400">
                  {j.customer_name || `#${j.id}`}
                </button>
              ))}
              <button type="button" onClick={() => allTo('')} className="px-2 py-1 text-gray-500">none</button>
            </div>
          )}
        </div>

        <ul className="flex-1 overflow-y-auto px-5 py-2 divide-y divide-gray-50">
          {rows.map((r, i) => {
            const onJob = cats.needsJob(r.category);
            const part = r.full > 0 && Number(r.amount) > 0 && Number(r.amount) < r.full - 0.005;
            return (
              <li key={i} className="py-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                {byLine && (
                  <span className="w-full text-sm text-gray-900 break-words">
                    {r.description}
                  </span>
                )}
                <div className="flex items-center gap-1">
                  <span className="text-gray-500 text-xs">$</span>
                  <input type="number" min={0} step="0.01" value={r.amount} onChange={e => set(i, 'amount', e.target.value)}
                    className={`w-20 border rounded-lg px-2 py-1 text-sm tabular-nums ${part ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`} />
                  {part && (
                    <button type="button" onClick={() => set(i, 'amount', r.full.toFixed(2))}
                      className="text-[11px] text-amber-700 whitespace-nowrap" title="Charge the whole line">
                      of {money(r.full)}
                    </button>
                  )}
                </div>
                <select value={r.category} onChange={e => set(i, 'category', e.target.value)}
                  className="border border-gray-200 rounded-lg px-1.5 py-1 text-xs bg-white">
                  {cats.grouped.map(g => (
                    <optgroup key={g.group} label={g.group}>
                      {g.items.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </optgroup>
                  ))}
                </select>
                {onJob ? (
                  <select value={r.job_id} onChange={e => set(i, 'job_id', e.target.value)}
                    className="flex-1 min-w-[8rem] border border-gray-200 rounded-lg px-1.5 py-1 text-xs bg-white">
                    <option value="">pick a job…</option>
                    {jobs.map(j => <option key={j.id} value={j.id}>{j.customer_name || `#${j.id}`} · {day(j.date)}{j.service_type ? ` · ${j.service_type}` : ''}</option>)}
                  </select>
                ) : (
                  <span className="flex-1 min-w-[8rem] text-[11px] text-gray-500">not a job cost</span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="p-5 pt-3 border-t border-gray-100 space-y-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
            <span><b className="text-gray-900">{money(used)}</b> to {jobCount} job{jobCount === 1 ? '' : 's'}</span>
            {shelved > 0.01 && <span className="text-gray-500">· {money(shelved)} kept as a running cost</span>}
            {left - shelved > 0.01 && <span className="text-gray-500">· {money(left - shelved)} tax</span>}
            {left < -0.01 && <span className="text-red-600 font-semibold">· {money(-left)} more than the charge</span>}
          </div>
          {shelved > 0.01 && (
            <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
              <b>{money(shelved)} wasn't used up here.</b> It becomes a running cost and gets spread across every mow
              through overhead — so it still counts, just not against this one yard.
            </p>
          )}
          <div className="flex justify-end">
            <button onClick={() => save.mutate()} disabled={!filled.length || left < -0.01 || save.isLoading}
              className="press px-4 py-2 rounded-xl bg-brand-600 text-black text-sm font-semibold disabled:opacity-50">
              {save.isLoading ? 'Saving…' : jobCount ? `Send to ${jobCount} job${jobCount === 1 ? '' : 's'}` : 'Pick a job first'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The items on a purchase, shown right on the review row.
 *
 * Long receipts are folded to the few biggest lines — those are what tell you
 * whose yard it was — with the rest a click away.
 */
/** The class list as <optgroup>s, for any select. */
function ClassOptions({ groups }: { groups: { group: string; items: Cls[] }[] }) {
  return <>{groups.map(g => (
    <optgroup key={g.group} label={g.group}>
      {g.items.map(c => <option key={c.key} value={c.key} title={c.hint}>{c.label}</option>)}
    </optgroup>
  ))}</>;
}

function ReceiptLines({ items, classes, onClass }: { items: any[]; classes?: { group: string; items: Cls[] }[]; onClass?: (key: string, cls: string | null) => void }) {
  const [all, setAll] = useState(false);
  const sorted = [...items].sort((a, b) => Math.abs(Number(b.total) || 0) - Math.abs(Number(a.total) || 0));
  const shown = all ? sorted : sorted.slice(0, 4);
  const rest = sorted.length - shown.length;
  const label = (k?: string | null) => (classes || []).flatMap(g => g.items).find(c => c.key === k)?.label || '';
  return (
    <div className="mt-1.5 text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5">
      <ul className="space-y-0.5">
        {shown.map((i, n) => (
          <li key={n} className="flex items-center justify-between gap-3">
            <span className="min-w-0 break-words">
              {i.quantity > 1 ? <span className="text-gray-500">{i.quantity}× </span> : null}
              {i.description}
              {i.cls && !onClass ? <span className="text-gray-400"> · {label(i.cls)}</span> : null}
            </span>
            <span className="flex items-center gap-2 flex-shrink-0">
              {onClass && i.key && classes?.length ? (
                // Exactly what this line was. Blank = not classed yet.
                <select value={i.cls || ''} onChange={e => onClass(i.key, e.target.value || null)} aria-label={`What was ${i.description}`}
                  className="border border-gray-200 rounded px-1 py-0.5 text-[11px] bg-white max-w-[9rem]">
                  <option value="">{i.cls ? '' : 'what is it?'}</option>
                  <ClassOptions groups={classes} />
                </select>
              ) : null}
              <span className="text-gray-500 tabular-nums">{money(i.total)}</span>
            </span>
          </li>
        ))}
      </ul>
      {rest > 0 && (
        <button onClick={() => setAll(true)} className="mt-1 text-brand-600 font-medium">
          and {rest} more item{rest === 1 ? '' : 's'}
        </button>
      )}
    </div>
  );
}

const isHomeDepot = (tx: Tx) => /home\s*depot|homedepot/i.test(`${tx.merchant_name || ''} ${tx.name || ''}`);
const isAmazon = (tx: Tx) => /amazon|amzn/i.test(`${tx.merchant_name || ''} ${tx.name || ''}`);
// Online stores whose order emails are read (server/services/storeReceipts.STORES).
const ONLINE_STORES: { re: RegExp; name: string; url: string }[] = [
  { re: /leonard|amleo/i, name: 'A.M. Leonard', url: 'https://www.amleo.com/account/orders' },
  { re: /partselect|part select/i, name: 'PartSelect', url: 'https://www.partselect.com/user/self-service-summary/' },
  { re: /lawnmow|lmts/i, name: 'Lawnmower Tire Store', url: 'https://lawnmowertirestore.com/sales/order/history/' },
];
const onlineStore = (tx: Tx) => ONLINE_STORES.find(s => s.re.test(`${tx.merchant_name || ''} ${tx.name || ''}`)) || null;
/** A bank name with no store in it ("SIGNATURE PURCHASE") — could be any store's receipt. */
const isNameless = (tx: Tx) => (!tx.merchant_name || /^(signature|debit card|pos |point of sale|checkcard|card purchase)/i.test(tx.merchant_name))
  && /signature|pos purchase|pos debit|debit card|card purchase|checkcard|purchase authorized|point of sale/i.test(`${tx.merchant_name || ''} ${tx.name || ''}`);
/** A store whose emailed receipts the app reads — gets the store receipt screen. */
const readsEmail = (tx: Tx) => isHomeDepot(tx) || isAmazon(tx) || !!onlineStore(tx) || isNameless(tx);

/**
 * Emailed receipts still looking for their bank charge, and why. "Why wasn't
 * that Home Depot receipt saved?" — it was; it is here, waiting.
 */
function WaitingReceipts() {
  const { data } = useQuery('receipts-waiting', () => api.get('/expenses/receipts/waiting').then(r => r.data.receipts as any[]), { retry: false });
  if (!data?.length) return null;
  return (
    <details className="px-4 py-3 border-t border-gray-100">
      <summary className="text-xs text-gray-600 cursor-pointer">
        {data.length} emailed receipt{data.length === 1 ? '' : 's'} waiting for a bank charge
      </summary>
      <p className="mt-2 text-[11px] text-gray-500">
        These were read and saved. Each attaches itself when a charge with the same total comes through; if the bank
        named it oddly, tap <b>Split</b> on the charge and pick it.
      </p>
      <ul className="mt-2 divide-y divide-gray-100 border border-gray-200 rounded-lg text-xs">
        {data.map(r => (
          <li key={r.id} className="px-2.5 py-2 space-y-0.5">
            <div className="flex justify-between gap-3">
              <span className="font-medium text-gray-900">{r.merchant || r.source} · {r.purchased_at ? day(r.purchased_at) : 'no date'}</span>
              <span className="font-semibold tabular-nums">{r.total != null ? money(r.total) : '—'}</span>
            </div>
            {!!r.first?.length && <p className="text-gray-500 break-words">{r.first.join(', ')}{r.lines > r.first.length ? ` +${r.lines - r.first.length} more` : ''}</p>}
            <p className="text-amber-700 break-words">{r.why}</p>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * Amazon's order export — the real prices Amazon's emails leave out. Same two
 * steps as the Home Depot export: read and show the plan, then file.
 */
function AmazonImport({ onFiled }: { onFiled: () => void }) {
  const [plan, setPlan] = useState<any | null>(null);
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef<HTMLInputElement>(null);

  async function run(text: string, apply: boolean) {
    setBusy(true);
    try {
      const { data } = await api.post('/expenses/amazon/import-csv', { csv: text, apply });
      setPlan(data);
      if (apply) { toast.success(data.message); onFiled(); }
    } catch (e: any) {
      toast.error(whyFailed(e, 'Could not read that export'));
    } finally { setBusy(false); }
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[14rem]">
          <p className="text-sm font-semibold text-gray-900">Real prices from Amazon</p>
          <p className="text-xs text-gray-500">
            Amazon Business → Business Analytics → Orders report (CSV), or Amazon → Account → Request your data →
            Your Orders (Retail.OrderHistory.1.csv). Amazon's emails only give each order's total.
          </p>
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={busy}
          className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 disabled:opacity-50">
          {busy ? 'Reading…' : plan ? 'Different file' : 'Choose the export'}
        </button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
          onChange={async e => {
            const f = e.target.files?.[0]; e.target.value = '';
            if (!f) return;
            const text = await f.text();
            setCsv(text); run(text, false);
          }} />
      </div>
      {plan && (
        <>
          <p className="text-sm text-gray-700">{plan.message}</p>
          <ul className="max-h-72 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-lg text-xs">
            {plan.plan.map((p: any, n: number) => (
              <li key={`${p.order_id}-${n}`} className="px-2.5 py-1.5 space-y-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-20 text-gray-500">{p.date}</span>
                  <span className="font-medium">{money(p.total)}</span>
                  {p.charge
                    ? <span className="text-emerald-700">→ {money(p.charge.amount)} on {day(p.charge.date)}</span>
                    : <span className="text-amber-700">{p.skipped}</span>}
                  {p.filed && <span className="text-gray-500">· {p.filed}</span>}
                </div>
                <p className="text-gray-600 break-words">{p.items.map((i: any) => `${i.description} ${money(i.total)}`).join(' · ')}</p>
              </li>
            ))}
          </ul>
          {!plan.apply && plan.matched > 0 && (
            <button onClick={() => run(csv, true)} disabled={busy}
              className="press px-4 py-2 rounded-xl bg-brand-600 text-black text-sm font-semibold disabled:opacity-50">
              Put real prices on these {plan.matched}
            </button>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The Pro Xtra purchase-history export — everything bought before the receipt
 * emails were switched on. Shows the plan first; nothing is written until the
 * second button.
 */
function HomeDepotBackfill({ onFiled }: { onFiled: () => void }) {
  const [plan, setPlan] = useState<any | null>(null);
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef<HTMLInputElement>(null);

  async function run(text: string, apply: boolean) {
    setBusy(true);
    try {
      const { data } = await api.post('/expenses/home-depot/import-csv', { csv: text, apply });
      setPlan(data);
      if (apply) { toast.success(data.message); onFiled(); }
    } catch (e: any) {
      toast.error(whyFailed(e, 'Could not read that export'));
    } finally { setBusy(false); }
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[14rem]">
          <p className="text-sm font-semibold text-gray-900">Backfill from Home Depot</p>
          <p className="text-xs text-gray-500">
            homedepot.com → Purchase History → export the CSV. Everything before the receipt emails started.
          </p>
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={busy}
          className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 disabled:opacity-50">
          {busy ? 'Reading…' : plan ? 'Different file' : 'Choose the export'}
        </button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
          onChange={async e => {
            const f = e.target.files?.[0]; e.target.value = '';
            if (!f) return;
            const text = await f.text();
            setCsv(text); run(text, false);
          }} />
      </div>

      {plan && (
        <>
          <p className="text-sm text-gray-700">{plan.message}</p>
          <div className="flex flex-wrap gap-4 text-xs text-gray-600">
            <span>material {money(plan.totals.material)}</span>
            <span>shop supplies {money(plan.totals.supplies)}</span>
            <span>tools {money(plan.totals.tools)}</span>
            {plan.already > 0 && <span>{plan.already} already imported</span>}
          </div>
          <ul className="max-h-64 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-lg text-xs">
            {plan.plan.map((p: any) => (
              <li key={p.key} className="px-2.5 py-1.5 flex flex-wrap items-center gap-2">
                <span className="w-20 text-gray-500">{p.date}</span>
                <span className="w-10 text-gray-500">{p.lines}×</span>
                <span className="w-20 font-medium">{money(p.subtotal)}</span>
                {p.charge
                  ? <span className="text-emerald-700">→ {money(p.charge.amount)} charge{p.tax ? ` (${money(p.tax)} tax)` : ''}</span>
                  : <span className="text-amber-700">{p.skipped}</span>}
                {p.job_tag && <span className="text-brand-600">tag: {p.job_tag}</span>}
                {!!p.tools?.length && <span className="text-gray-500">{p.tools.length} tool(s)</span>}
                {p.filed && <span className="text-gray-500">· {p.filed}</span>}
              </li>
            ))}
          </ul>
          {!plan.apply && plan.matched > 0 && (
            <button onClick={() => run(csv, true)} disabled={busy}
              className="press px-4 py-2 rounded-xl bg-brand-600 text-black text-sm font-semibold disabled:opacity-50">
              File these {plan.matched} purchases
            </button>
          )}
        </>
      )}
    </div>
  );
}

/**
 * A Home Depot charge, sorted from Home Depot's own receipt.
 *
 * Home Depot keeps the itemised receipt for every in-store purchase made on a
 * card registered to Pro Xtra, so there is no reason to photograph the paper
 * one. Open Purchase History, copy the lines, and every item goes where it
 * belongs: the yard it was for, the shop, or the tool cupboard.
 */
function HomeDepotReceipt({ tx, onClose, onDone }: { tx: Tx; onClose: () => void; onDone: () => void }) {
  const [pasted, setPasted] = useState('');
  const [read, setRead] = useState<any | null>(null);
  const [jobLines, setJobLines] = useState<any[]>([]);
  const [shop, setShop] = useState<any[]>([]);
  const [tools, setTools] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);

  const { data: cand } = useQuery(['expense-candidates', tx.id], () =>
    api.get(`/expenses/${tx.id}/candidates`).then(r => r.data as { non_mowing: Candidate[]; mowing_that_day: Candidate[] }));
  const jobs = [...(cand?.non_mowing || []), ...(cand?.mowing_that_day || [])];

  const find = `${day(tx.date)} · ${money(tx.amount)}`;

  const amazon = isAmazon(tx);
  const other = onlineStore(tx);
  const nameless = isNameless(tx) && !amazon && !other;
  const store = amazon ? 'Amazon' : other ? other.name : nameless ? 'Store' : 'Home Depot';
  const siteUrl = amazon ? 'https://www.amazon.com/gp/css/order-history' : other ? other.url : 'https://www.homedepot.com/myaccount/purchaseHistory';
  const siteLabel = amazon ? 'amazon.com your orders' : other ? `${other.name} order history` : 'homedepot.com purchase history';
  // Receipts the store already emailed that haven't found their charge yet.
  const { data: emailed } = useQuery(['hd-emailed', tx.id], () =>
    api.get(`/expenses/${tx.id}/home-depot/emailed`).then(r => r.data.receipts as any[]), { retry: false });

  function show(data: any) {
    setRead(data);
    setJobLines((data.sorted?.to_job || []).map((l: any) => ({ ...l, job_id: '' })));
    setShop(data.sorted?.to_shop || []);
    setTools((data.sorted?.to_tools || []).map((t: any) => ({ ...t, life_months: 48 })));
    if (data.check?.warning) toast.warn(data.check.warning); else toast.success(data.message);
  }

  async function readIt() {
    if (!pasted.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/expenses/${tx.id}/home-depot`, { text: pasted });
      show(data);
    } catch (e: any) {
      toast.error(whyFailed(e, 'Could not read that'));
    } finally { setBusy(false); }
  }

  async function takeEmailed(receiptId: number) {
    setBusy(true);
    try {
      const { data } = await api.post(`/expenses/${tx.id}/home-depot/emailed/${receiptId}`);
      show(data);
    } catch (e: any) {
      toast.error(whyFailed(e, 'Could not use that receipt'));
    } finally { setBusy(false); }
  }

  const assigned = jobLines.filter(l => l.job_id);
  const shopTotal = shop.reduce((s, l) => s + Number(l.total || 0), 0);
  const toolTotal = tools.reduce((s, t) => s + Number(t.total || 0), 0);
  const filed = assigned.reduce((s, l) => s + Number(l.total || 0), 0) + shopTotal + toolTotal;
  const leftover = Math.round((Number(tx.amount) - filed) * 100) / 100;

  const save = useMutation(
    () => api.post(`/expenses/${tx.id}/home-depot/apply`, {
      receipt_id: read?.receipt_id,
      to_job: assigned.map(l => ({ description: l.description, amount: l.total, category: l.category, job_id: Number(l.job_id) })),
      shop_total: shopTotal,
      to_tools: tools,
    }),
    { onSuccess: (r: any) => { toast.success(r.data.message); if (r.data.note) toast.info(r.data.note); onDone(); },
      onError: (e: any) => { toast.error(whyFailed(e, 'Could not file that')); } });

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl p-5 space-y-3 max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-bold text-gray-900">{store} receipt</h3>
            <p className="text-xs text-gray-500">{tx.merchant_name || tx.name} · {find}</p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>

        {!read ? (
          <div className="space-y-3">
            {!!emailed?.length && (
              <div className="rounded-xl border border-brand-200 bg-brand-50 p-3 space-y-2">
                <p className="text-sm font-semibold text-brand-900">{nameless ? 'Receipts emailed around this date' : `${store} already emailed these`} — tap the one for this charge</p>
                <ul className="space-y-1.5">
                  {emailed.map(r => (
                    <li key={r.id}>
                      <button type="button" onClick={() => takeEmailed(r.id)} disabled={busy}
                        className="press w-full text-left bg-white rounded-lg border border-brand-100 px-3 py-2 disabled:opacity-50">
                        <span className="flex justify-between gap-3 text-sm">
                          <span className="font-medium text-gray-900">{r.purchased_at ? day(r.purchased_at) : 'No date'}{r.store ? ` · ${r.store}` : ''}</span>
                          <span className={`font-semibold ${r.same_amount ? 'text-brand-700' : 'text-gray-900'}`}>{r.total != null ? money(r.total) : '—'}{r.same_amount ? ' ✓' : ''}</span>
                        </span>
                        <span className="block text-[11px] text-gray-500 break-words">{r.lines} line{r.lines === 1 ? '' : 's'}{r.first?.length ? ` · ${r.first.join(', ')}` : ''}</span>
                      </button>
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] text-brand-800">Not here? Paste it from their site below.</p>
              </div>
            )}
            <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
              <li>
                Open your receipts on their site —{' '}
                <a href={siteUrl} target="_blank" rel="noreferrer"
                  className="text-brand-600 font-semibold underline">{siteLabel}</a>
              </li>
              <li>Find <b className="whitespace-nowrap">{find}</b>, hit <b>{amazon ? 'View invoice' : 'View Receipt'}</b></li>
              <li>Select the item lines, copy them, paste below</li>
            </ol>
            <textarea value={pasted} onChange={e => setPasted(e.target.value)} rows={7}
              placeholder={`Paste the receipt from ${store} here…`}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
            <p className="text-[11px] text-gray-500">
              Nothing about your {store} login is stored here — the app only reads what you paste.
              {!amazon && !other && <> If a purchase is missing from that page, the card wasn't registered to your Pro&nbsp;Xtra account;
              register it once and every purchase after that shows up on its own.</>}
            </p>
            <div className="flex justify-end">
              <button onClick={readIt} disabled={!pasted.trim() || busy}
                className="press px-4 py-2.5 rounded-xl bg-brand-600 text-black text-sm font-semibold disabled:opacity-50">
                {busy ? 'Reading…' : 'Read and sort it'}
              </button>
            </div>
          </div>
        ) : (
          <>
            {read.check?.warning && (
              <p className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2">{read.check.warning}</p>
            )}
            {read.order_number && <p className="text-xs text-gray-500">Receipt {read.order_number}{read.store ? ` · ${read.store}` : ''}</p>}

            {!!jobLines.length && (
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1">Went into a yard — pick which</p>
                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
                  {jobLines.map((l, idx) => (
                    <li key={idx} className="p-2.5 flex flex-wrap items-center gap-2">
                      <div className="flex-1 min-w-[10rem]">
                        <p className="text-sm text-gray-900">{l.description}</p>
                        <p className="text-[11px] text-gray-500">{l.kind}{l.quantity ? ` · ${l.quantity}` : ''}{l.sku ? ` · ${l.sku}` : ''}</p>
                      </div>
                      <span className="text-sm font-semibold w-20 text-right">{money(l.total)}</span>
                      <select value={l.job_id} onChange={e => setJobLines(v => v.map((x, i) => i === idx ? { ...x, job_id: e.target.value } : x))}
                        className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs max-w-[12rem]">
                        <option value="">pick a job…</option>
                        {jobs.map(j => <option key={j.id} value={j.id}>{j.customer_name || `#${j.id}`} · {day(j.date)}{j.service_type ? ` · ${j.service_type}` : ''}</option>)}
                      </select>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!!shop.length && (
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1">Shop supplies — overhead, no job to pick ({money(shopTotal)})</p>
                <ul className="text-sm text-gray-600 border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {shop.map((l, i) => <li key={i} className="px-2.5 py-1.5 flex justify-between"><span>{l.description}</span><span>{money(l.total)}</span></li>)}
                </ul>
              </div>
            )}

            {!!tools.length && (
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1">Tools — added to your equipment and spread over their life</p>
                <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {tools.map((t, idx) => (
                    <li key={idx} className="p-2.5 flex flex-wrap items-center gap-2">
                      <span className="flex-1 text-sm text-gray-900 min-w-[9rem]">{t.description}</span>
                      <span className="text-sm font-semibold">{money(t.total)}</span>
                      <label className="text-[11px] text-gray-500 flex items-center gap-1">
                        lasts
                        <input type="number" min={6} max={240} value={t.life_months}
                          onChange={e => setTools(v => v.map((x, i) => i === idx ? { ...x, life_months: Number(e.target.value) } : x))}
                          className="w-16 border border-gray-300 rounded px-1.5 py-1 text-xs" />
                        months → {money(Number(t.total) / Math.max(1, Number(t.life_months) || 48))}/mo
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
              <span>{money(filed)} of {money(tx.amount)} filed</span>
              {leftover > 0.01 && <span className="text-gray-500">· {money(leftover)} tax or left off</span>}
              {leftover < -0.01 && <span className="text-red-600 font-semibold">· more than the charge</span>}
              <div className="ml-auto flex gap-2">
                <button onClick={async () => {
                  if (read?.receipt_id) await api.delete(`/expenses/receipt/${read.receipt_id}`).catch(() => {});
                  setRead(null); setPasted('');
                }} className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600">Paste again</button>
                <button onClick={() => save.mutate()} disabled={leftover < -0.01 || save.isLoading || (!assigned.length && !shop.length && !tools.length)}
                  className="press px-3 py-1.5 rounded-xl bg-brand-600 text-black font-semibold disabled:opacity-50">
                  {save.isLoading ? 'Filing…' : 'File it'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ReceiptSplitter({ tx, onClose, onDone }: { tx: Tx; onClose: () => void; onDone: () => void }) {
  const cats = useCategories();
  const [receipt, setReceipt] = useState<any | null>(null);
  const [lines, setLines] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const { data: cand } = useQuery(['expense-candidates', tx.id], () =>
    api.get(`/expenses/${tx.id}/candidates`).then(r => r.data as { non_mowing: Candidate[]; mowing_that_day: Candidate[] }));
  const jobs = [...(cand?.non_mowing || []), ...(cand?.mowing_that_day || [])];

  useQuery(['expense-receipt', tx.id], () => api.get(`/expenses/${tx.id}/receipt`).then(r => r.data.receipt), {
    onSuccess: (r: any) => { if (r && !receipt) { setReceipt(r); setLines((r.items || []).map((i: any) => ({ ...i, job_id: '' }))); } },
  });

  async function upload(file: File) {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('receipt', file);
      const { data } = await api.post(`/expenses/${tx.id}/receipt`, fd);
      setReceipt(data);
      setLines((data.items || []).map((i: any) => ({ ...i, job_id: '' })));
      if (data.check?.warning) toast.warn(data.check.warning); else toast.success(data.message);
    } catch (e: any) {
      toast.error(whyFailed(e, 'Could not read that receipt'));
    } finally { setBusy(false); }
  }

  const assigned = lines.filter(l => l.job_id && l.expense_category);
  const sum = assigned.reduce((s, l) => s + Number(l.total || 0), 0);
  const leftover = Math.round((Number(tx.amount) - sum) * 100) / 100;

  const save = useMutation(
    () => api.post(`/expenses/${tx.id}/split`, {
      receipt_id: receipt?.receipt_id || receipt?.id,
      lines: assigned.map(l => ({ description: l.description, amount: l.total, category: l.expense_category, job_id: Number(l.job_id) })),
    }),
    { onSuccess: (r: any) => { toast.success(r.data.message); onDone(); },
      onError: (e: any) => { toast.error(whyFailed(e, 'Could not split that')); } });

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl p-5 space-y-3 max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-bold text-gray-900">Split this receipt</h3>
            <p className="text-xs text-gray-500">{tx.merchant_name || tx.name} · {day(tx.date)} · {money(tx.amount)}</p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>

        {!receipt ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-600 mb-3">Photograph the till receipt and each line can go to the job it was for.</p>
            <button onClick={() => fileRef.current?.click()} disabled={busy}
              className="press px-4 py-2.5 rounded-xl bg-brand-600 text-black text-sm font-semibold flex items-center gap-2 mx-auto disabled:opacity-50">
              <Upload className="h-4 w-4" /> {busy ? 'Reading…' : 'Upload the receipt'}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
          </div>
        ) : (
          <>
            {receipt.check?.warning && (
              <p className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2">{receipt.check.warning}</p>
            )}
            {receipt.readable === false && (
              <p className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2">That photo was hard to read — check the lines below before saving.</p>
            )}
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
              {lines.map((l, idx) => (
                <li key={idx} className="p-2.5 flex flex-wrap items-center gap-2">
                  <div className="flex-1 min-w-[10rem]">
                    <p className="text-sm text-gray-900">{l.description}</p>
                    <p className="text-[11px] text-gray-500">{l.kind}{l.quantity ? ` · ${l.quantity}` : ''}</p>
                  </div>
                  <span className="text-sm font-semibold w-20 text-right">{money(l.total)}</span>
                  <select value={l.expense_category || ''} onChange={e => setLines(v => v.map((x, i) => i === idx ? { ...x, expense_category: e.target.value } : x))}
                    className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs">
                    <option value="">not a job cost</option>
                    {cats.onJob.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                  <select value={l.job_id} onChange={e => setLines(v => v.map((x, i) => i === idx ? { ...x, job_id: e.target.value } : x))}
                    className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs max-w-[12rem]">
                    <option value="">pick a job…</option>
                    {jobs.map(j => <option key={j.id} value={j.id}>{j.customer_name || `#${j.id}`} · {day(j.date)}{j.service_type ? ` · ${j.service_type}` : ''}</option>)}
                  </select>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
              <span>{assigned.length} of {lines.length} lines assigned</span>
              <span>· {money(sum)} of {money(tx.amount)}</span>
              {leftover > 0.01 && <span className="text-gray-500">· {money(leftover)} left off (tax or overhead)</span>}
              {leftover < -0.01 && <span className="text-red-600 font-semibold">· more than the charge</span>}
              <div className="ml-auto flex gap-2">
                <button onClick={() => fileRef.current?.click()} className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600">Different photo</button>
                <button onClick={() => save.mutate()} disabled={!assigned.length || leftover < -0.01 || save.isLoading}
                  className="press px-3 py-1.5 rounded-xl bg-brand-600 text-black font-semibold disabled:opacity-50">
                  {save.isLoading ? 'Saving…' : 'Save the split'}
                </button>
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Every money-out dollar in its parts — each receipt line, each job cost, what was kept back — by class and
 * month, with what still needs the owner. Reads GET /expenses/by-class; the two buttons write the classes
 * the rules are sure of (and, separately, the best guesses) through POST /expenses/classify-guesses.
 */
function ByClass({ range, onChanged }: { range: { start: string; end: string }; onChanged: () => void }) {
  const readOnly = useReadOnly();
  const params = { ...(range.start ? { start: range.start } : {}), ...(range.end ? { end: range.end } : {}) };
  const { data, isLoading, isError, refetch } = useQuery(['expenses-by-class', params], () => api.get('/expenses/by-class', { params }).then(r => r.data), { retry: false });
  const [busy, setBusy] = useState<string | null>(null);
  const [showNeeds, setShowNeeds] = useState(false);
  async function classify(only: string[], what: string) {
    setBusy(what);
    try {
      const dry = await api.post('/expenses/classify-guesses', { only, apply: false, ...params });
      const n = dry.data?.plan ? dry.data.plan.lines + dry.data.plan.items + dry.data.plan.job_expenses : 0;
      if (!n) { toast.info('Nothing left to classify that way'); return; }
      if (!window.confirm(`${dry.data.message}. Write them?`)) return;
      const r = await api.post('/expenses/classify-guesses', { only, apply: true, ...params });
      toast.success(r.data?.message || 'Done');
      refetch(); onChanged();
    } catch (e: any) { toast.error(whyFailed(e, 'Could not classify')); }
    finally { setBusy(null); }
  }
  if (isLoading) return <p className="mt-2 text-xs text-gray-500">Adding up every line…</p>;
  if (isError || !data) return <p className="mt-2 text-xs text-red-600">Could not add up the classes. <button type="button" onClick={() => refetch()} className="underline">Try again</button></p>;
  const months: string[] = data.months || [];
  const mon = (m: string) => new Date(`${m}-02T12:00:00`).toLocaleDateString('en-US', { month: 'short' });
  const t = data.totals || {};
  return (
    <div className="mt-3 space-y-3">
      <p className="text-xs text-gray-600">
        {money(t.parts_out)} out in {t.parts} parts{Math.abs(Number(t.difference) || 0) > 0.02 ? <span className="text-amber-700"> — {money(t.difference)} off the bank feed</span> : ' — matches the bank feed to the penny'}.
        {' '}{t.set} set by you, {t.guessed} guessed by the rules, <b>{t.needs_n} only you can name ({money(t.needs_you)})</b>.
      </p>
      {!readOnly && (
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={!!busy || !data.sure} onClick={() => classify(['rule'], 'sure')} className="press text-xs px-3 py-1.5 rounded-lg bg-brand-600 text-black font-semibold disabled:opacity-50">
            {busy === 'sure' ? 'Writing…' : `Write what the rules are sure of (${data.sure})`}
          </button>
          <button type="button" disabled={!!busy || !data.guesses} onClick={() => classify(['judgment'], 'guess')} className="press text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-800 font-semibold disabled:opacity-50">
            {busy === 'guess' ? 'Writing…' : `…and the best guesses (${data.guesses})`}
          </button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="text-[11px] w-full min-w-[28rem]">
          <thead><tr className="text-gray-500"><th className="text-left font-medium py-1">Class</th>{months.map(m => <th key={m} className="text-right font-medium py-1 pl-2">{mon(m)}</th>)}<th className="text-right font-medium py-1 pl-2">Total</th></tr></thead>
          <tbody>
            {(data.groups || []).map((g: any) => (
              <React.Fragment key={g.group}>
                <tr className="bg-gray-50"><td className="font-semibold text-gray-800 py-1" colSpan={months.length + 2}>{g.group} · {money(g.total)}</td></tr>
                {(data.rows || []).filter((r: any) => r.group === g.group).map((r: any) => (
                  <tr key={r.cls} className="border-t border-gray-100">
                    <td className="py-0.5 text-gray-700">{r.label}{r.needs_you ? <span className="text-amber-700"> · {r.needs_you} to name</span> : null}</td>
                    {months.map(m => <td key={m} className="text-right tabular-nums text-gray-600 pl-2">{r.by_month[m] ? money(r.by_month[m]) : ''}</td>)}
                    <td className="text-right tabular-nums font-medium text-gray-900 pl-2">{money(r.total)}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {t.needs_n > 0 && (
        <div>
          <button type="button" onClick={() => setShowNeeds(v => !v)} className="text-xs font-semibold text-brand-700 underline">{showNeeds ? 'Hide' : 'Show'} what only you can name ({t.needs_n})</button>
          {showNeeds && (
            <ul className="mt-1.5 space-y-1 text-[11px] text-gray-700">
              {(data.needs || []).slice(0, 60).map((p: any) => (
                <li key={p.n} className="flex justify-between gap-3 border-t border-gray-100 py-1">
                  <span className="min-w-0"><b>{p.date?.slice(5)} · {p.vendor}</b>{p.item && p.item !== p.vendor ? ` · ${p.item}` : ''}<span className="block text-gray-500">{p.note} (guess: {p.label})</span></span>
                  <span className="tabular-nums flex-shrink-0">{money(p.amount)}</span>
                </li>
              ))}
              {(data.needs || []).length > 60 && <li className="text-gray-500">and {(data.needs || []).length - 60} more — sort them above, newest first</li>}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
